<template>
    <page-layout>
        <template #header>
            <step-header
                :current-step="stepNumber"
                :total-steps="totalSteps"
                :title="pageTitle"
                :use-exit-modal="false"
                :is-loading="isLoading"
                :skip-steps="isBriefAccepted"
                :is-preview="isPreview"
            />
        </template>
        <template #default>
            <g-loader v-if="isLoading" type="fixed" />
            <div v-else>
                <error-message-card v-if="errorMessage" class="margin-bottom-normal" :message="errorMessage" />
                <step-error v-if="step && step.error" class="margin-bottom-normal" :step="step" />
                <div class="margin-bottom-regular">
                    <g-typography tag="p" :value="stepOverview" />
                </div>
                <div v-if="brief.has_contract_payments">
                    <div class="margin-bottom-regular">
                        <g-typography tag="p" :value="stepTitle" font-weight="semibold" />
                    </div>
                    <div class="margin-bottom-regular">
                        <g-typography tag="p" :value="paymentsStepOverview" />
                    </div>
                    <payments-list v-if="!influencerProposesPrice" :brief="brief" class="margin-bottom-regular" />
                    <div v-if="influencerProposesPrice" class="margin-bottom-regular">
                        <div v-if="(brief && Array.isArray(brief.payment_configs) ? brief.payment_configs.length : 0) == 0">
                            <g-money-input
                                v-model="price"
                                :currency="brief.activation.currency"
                                :min="0"
                                :disabled="!isBriefEditable"
                                data-testid="creator_proposed_amount"
                            />
                        </div>
                        <div v-else-if="(brief && Array.isArray(brief.payment_configs) ? brief.payment_configs.length : 0) == 1">
                            <g-money-input
                                data-testid="customized-payment-price"
                                :disabled="!isBriefEditable"
                                v-model="price"
                                :currency="brief.activation.currency"
                                :min="0"
                            />
                        </div>
                        <div v-else v-for="payment_config in (brief && Array.isArray(brief.payment_configs) ? brief.payment_configs : [])" :key="payment_config.dollar_amount">
                            <g-money-input
                                :disabled="!isBriefEditable"
                                :value="payment_config.dollar_amount"
                                :currency="brief.activation.currency"
                                :min="0"
                            />
                        </div>
                    </div>
                </div>
                <div v-if="brief.has_commission_payments">
                    <div class="margin-bottom-regular">
                        <g-typography tag="p" value="Commissions" font-weight="semibold" />
                    </div>
                    <div class="margin-bottom-regular">
                        <g-typography tag="p" :value="commissionsStepOverview" />
                    </div>
                    <div v-if="affiliateLinks.length > 0" class="margin-bottom-regular">
                        <affiliate-links-display :affiliate-links="affiliateLinks" />
                    </div>
                    <g-base-box v-if="discountCodeString" class="commissions-box">
                        <g-typography font-weight="semibold" value="Your Code: " />
                        <g-typography :value="discountCodeString" />
                    </g-base-box>
                </div>
            </div>
        </template>
        <template #footer>
            <step-footer
                v-if="isBriefEditable"
                :is-next-disabled="false"
                :step="step"
                :loading="isCompletingBrief"
                :use-exit-modal="false"
                :step-completed="isCompleted"
                :current-step="stepNumber"
                :total-steps="totalSteps"
                :is-preview="isPreview"
                @go-next-step="goNextStep"
                @go-previous-step="goPreviousStep"
            />
        </template>
    </page-layout>
</template>

<script>
    import { join, get } from 'lodash';
    import {
        CONTRACT_PAYMENTS_MODE_PER_TASK,
        CONTRACT_PAYMENTS_MODE_PROPOSED
    } from '../../../constants/activation-payments';
    import { customizableText } from '../../../constants/activation-brief-default-copy';
    import { getCustomizableText, getCustomizableTitle } from '../../../helpers/activations/default-texts';
    import PageLayout from '../../layouts/PageLayout.vue';
    import StepError from './partials/StepError.vue';
    import StepHeader from './partials/StepHeader.vue';
    import StepFooter from './partials/StepFooter.vue';
    import PaymentsList from './partials/payments/PaymentsList.vue';
    import ErrorMessageCard from './partials/ErrorMessageCard.vue';
    import ActivationBriefMixin from './mixins/ActivationBriefMixin';
    import AffiliateLinksDisplay from './partials/AffiliateLinksDisplay.vue';

    export default {
        name: 'ActivationBriefPaymentsView',
        components: {
            AffiliateLinksDisplay,
            PaymentsList,
            StepFooter,
            StepHeader,
            StepError,
            ErrorMessageCard,
            PageLayout
        },
        mixins: [ActivationBriefMixin],
        props: {
            brief: {
                type: Object,
                default: null
            },
            step: {
                type: Object,
                default: null
            },
            isLoading: {
                type: Boolean,
                default: false
            },
            isCompletingBrief: {
                type: Boolean,
                default: false
            },
            errors: {
                type: Object,
                default: () => ({})
            },
            errorMessage: {
                type: String,
                default: ''
            },
            affiliateLinks: {
                type: Array,
                default: () => []
            },
            discountCodes: {
                type: Array,
                default: () => []
            }
        },
        data() {
            return {
                price: null
            };
        },
        computed: {
            stepTitle() {
                return [CONTRACT_PAYMENTS_MODE_PER_TASK, CONTRACT_PAYMENTS_MODE_PROPOSED].includes(
                    this.brief && this.brief.contract_payments_mode
                )
                    ? 'Payments'
                    : 'Fixed Payments';
            },
            pageTitle() {
                return getCustomizableTitle(this.brief, 'live_payment_title');
            },
            stepOverview() {
                return getCustomizableText(this.brief, 'live_payment_description');
            },
            paymentsStepOverview() {
                let defaultDescription = customizableText.live_contract_payments_description;

                if (this.brief?.contract_payments_mode === CONTRACT_PAYMENTS_MODE_PER_TASK) {
                    defaultDescription = customizableText.live_contract_payments_description_per_task;
                } else if (this.brief?.contract_payments_mode === CONTRACT_PAYMENTS_MODE_PROPOSED) {
                    defaultDescription = customizableText.live_contract_payments_description_proposed;
                }

                return getCustomizableText(this.brief, 'live_contract_payments_description', defaultDescription);
            },
            commissionsStepOverview() {
                return getCustomizableText(this.brief, 'live_commission_payments_description');
            },
            stepNumber() {
                return get(this, 'step.meta.stepNumber', 0);
            },
            totalSteps() {
                return get(this, 'step.meta.totalSteps', 0);
            },
            isCompleted() {
                return get(this, 'step.is_complete', false);
            },
            influencerProposesPrice() {
                return this.brief.contract_payments_mode === CONTRACT_PAYMENTS_MODE_PROPOSED;
            },
            discountCodeString() {
                const conversionConfigs = this.brief.conversion_configs.map((config) =>
                    config.is_displayed ? config.discount_group_id : -1
                );
                const codes = this.discountCodes
                    .filter((code) => conversionConfigs.includes(code.group_id))
                    .map((code) => code.code);
                return codes ? join(codes, ', ') : null;
            },
            initialPrice() {
                return get(this, 'brief.payables[0].amount', null);
            },
            paymentConfig() {
                return get(this, 'brief.payment_configs[0].dollar_amount', null);
            }
        },
        methods: {
            goNextStep() {
                if (this.influencerProposesPrice) {
                    this.$emit('go-next-step', this.step, { price: this.price });
                }
                this.$emit('go-next-step', this.step);
            },
            goPreviousStep() {
                if (this.influencerProposesPrice) {
                    this.$emit('go-previous-step', this.step, { price: this.price });
                }
                this.$emit('go-previous-step', this.step);
            }
        },

        watch: {
            initialPrice: {
                immediate: true,
                handler() {
                    this.price = this.initialPrice;
                }
            },
            paymentConfig: {
                immediate: true,
                handler() {
                    this.price = this.paymentConfig;
                }
            }
        }
    };
</script>

<style lang="scss" scoped>
    .margin-bottom-large {
        margin-bottom: $g-spacing-6;
    }

    .margin-bottom-regular {
        margin-bottom: $g-spacing-3;
    }

    .commissions-box {
        background-color: $g-color-grey-2 !important;
    }
</style>
